import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../supabase';

/**
 * Newsletter e email automatiche inviate a un contatto, per la cronologia
 * della sua scheda.
 *
 * La fonte è `email_queue`: una riga per destinatario, con oggetto, campagna e le
 * date di consegna, apertura, click, rimbalzo e disiscrizione già risolte dagli
 * eventi del provider. La tabella è isolata per organizzazione da RLS
 * (`email_queue_tenant_isolation`), quindi si legge direttamente dal frontend.
 *
 * L'HTML spedito NON viene caricato con la lista: sono decine di KB per riga e
 * servono solo a chi apre l'anteprima. Si prende a parte con `loadPreview`.
 */

export interface ClientNewsletter {
  id: string;
  /** `newsletter` = campagna; `workflow` = email mandata da un'automazione. */
  source: 'newsletter' | 'workflow';
  /** Solo per le email delle automazioni: quale workflow l'ha mandata. */
  workflowId: string | null;
  workflowName: string | null;
  subject: string | null;
  campaignName: string | null;
  status: string | null;
  sentAt: string | null;
  deliveredAt: string | null;
  openedAt: string | null;
  clickedAt: string | null;
  bouncedAt: string | null;
  unsubscribedAt: string | null;
  errorMessage: string | null;
  /** Data usata in cronologia: invio, altrimenti creazione della riga. */
  date: string;
}

interface Riga {
  id: string;
  recipient_email: string;
  source_type: string | null;
  source_id: string | null;
  subject: string | null;
  status: string | null;
  sent_at: string | null;
  delivered_at: string | null;
  opened_at: string | null;
  clicked_at: string | null;
  bounced_at: string | null;
  unsubscribed_at: string | null;
  error_message: string | null;
  created_at: string;
  newsletter_campaigns: { name: string | null } | { name: string | null }[] | null;
}

const LIMITE = 20;

export function useClientNewsletters(email: string | null | undefined, organizationId: string | null | undefined) {
  const [newsletters, setNewsletters] = useState<ClientNewsletter[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!email || !organizationId) {
      setNewsletters([]);
      return;
    }
    let annullato = false;
    (async () => {
      setLoading(true);
      // `ilike` interpreta `%` e `_` come jolly e negli indirizzi il trattino basso
      // è comune (mario_rossi@… pescherebbe anche mario-rossi@…): si protegge il
      // pattern e poi si confronta comunque per intero, senza distinzione di caso.
      const pattern = email.trim().replace(/([\\%_])/g, '\\$1');
      const atteso = email.trim().toLowerCase();
      const { data, error } = await supabase
        .from('email_queue')
        .select(
          'id, recipient_email, source_type, source_id, subject, status, sent_at, delivered_at, opened_at, clicked_at, bounced_at, unsubscribed_at, error_message, created_at, newsletter_campaigns(name)'
        )
        .eq('organization_id', organizationId)
        .in('source_type', ['newsletter', 'workflow'])
        .ilike('recipient_email', pattern)
        .order('created_at', { ascending: false })
        .limit(LIMITE);
      if (annullato) return;
      if (error) {
        console.error('[newsletter cronologia] lettura fallita:', error.message);
        setNewsletters([]);
      } else {
        const righe = (data as Riga[] | null)?.filter((r) => (r.recipient_email || '').trim().toLowerCase() === atteso) ?? [];

        // Le email delle automazioni portano in `source_id` l'id del workflow, senza
        // chiave esterna: i nomi si prendono a parte, una volta sola per tutte.
        const idWorkflow = [...new Set(righe.filter((r) => r.source_type === 'workflow' && r.source_id).map((r) => r.source_id as string))];
        const nomiWorkflow = new Map<string, string>();
        if (idWorkflow.length > 0) {
          const { data: wf } = await supabase.from('automation_workflows').select('id, name').eq('organization_id', organizationId).in('id', idWorkflow);
          if (annullato) return;
          (wf ?? []).forEach((w: any) => nomiWorkflow.set(w.id, w.name));
        }

        setNewsletters(
          righe.map((r) => {
            const campagna = Array.isArray(r.newsletter_campaigns) ? r.newsletter_campaigns[0] : r.newsletter_campaigns;
            const daWorkflow = r.source_type === 'workflow';
            return {
              id: r.id,
              source: daWorkflow ? 'workflow' : 'newsletter',
              workflowId: daWorkflow ? r.source_id : null,
              workflowName: daWorkflow && r.source_id ? nomiWorkflow.get(r.source_id) ?? null : null,
              subject: r.subject,
              campaignName: campagna?.name ?? null,
              status: r.status,
              sentAt: r.sent_at,
              deliveredAt: r.delivered_at,
              openedAt: r.opened_at,
              clickedAt: r.clicked_at,
              bouncedAt: r.bounced_at,
              unsubscribedAt: r.unsubscribed_at,
              errorMessage: r.error_message,
              date: r.sent_at || r.created_at,
            };
          })
        );
      }
      setLoading(false);
    })();
    return () => { annullato = true; };
  }, [email, organizationId]);

  return { newsletters, loading };
}

/** Contenuto dell'anteprima: l'HTML davvero spedito e i link cliccati. */
export interface NewsletterPreview {
  html: string | null;
  clickedUrls: string[];
}

export function useNewsletterPreview(queueId: string | null) {
  const [preview, setPreview] = useState<NewsletterPreview | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (id: string) => {
    setLoading(true);
    const [{ data: riga }, { data: eventi }] = await Promise.all([
      supabase.from('email_queue').select('rendered_html, html_content').eq('id', id).maybeSingle(),
      supabase.from('email_events').select('clicked_url').eq('email_queue_id', id).not('clicked_url', 'is', null),
    ]);
    setPreview({
      html: riga?.rendered_html || riga?.html_content || null,
      clickedUrls: [...new Set((eventi ?? []).map((e: any) => e.clicked_url).filter(Boolean))] as string[],
    });
    setLoading(false);
  }, []);

  useEffect(() => {
    if (queueId) void load(queueId);
    else setPreview(null);
  }, [queueId, load]);

  return { preview, loading };
}
